console.log("[BOOT] Build V3 loaded at 2026-03-05T18:00 — bundle fingerprint active");
import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App.tsx';
import './index.css';
import './i18n'; // Initialise i18next before App renders
import { initVersionCheck, forceAppRefresh } from './utils/app-version.ts';
import { isPushActivitySuspended } from './utils/pwa/push-activity.ts';
import { toast } from 'sonner';
import { isPreviewHost } from './utils/device.ts';

// Register service worker for PWA functionality
async function registerServiceWorker() {
  if ('serviceWorker' in navigator) {
    try {
      console.log('Registering service worker...');
      
      // Register service worker normally — do NOT unregister existing ones,
      // as that destroys the "waiting worker" state needed for updates.
      const registration = await navigator.serviceWorker.register(`/sw.js`, {
        scope: '/',
        updateViaCache: 'none'
      });
      
      console.log('Service Worker registered with scope:', registration.scope);
      
      // Check if there's a waiting worker (update ready to activate)
      if (registration.waiting) {
        console.log('New service worker waiting');
        notifyUserAboutUpdate();
      }

      // Handle new SW installation
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (!newWorker) return;

        console.log('New service worker installing...');
        
        // Listen for state changes
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            console.log('New service worker installed and waiting');
            notifyUserAboutUpdate();
          }
        });
      });
      
      // Listen for controller change events
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        console.log('Service Worker controller changed');
        if (isPushActivitySuspended()) {
          console.log('[SW] Deferring controller-change reload during push activity');
          return;
        }
        void forceAppRefresh();
      });
      
      // Check for updates every 15 minutes
      setInterval(() => {
        registration.update().catch(console.error);
      }, 15 * 60 * 1000);
      
      // Listen for messages from service worker
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'UPDATE_AVAILABLE') {
          console.log('Update message from service worker received');
          if (isPushActivitySuspended()) {
            console.log('[SW] Ignoring update prompt during push activity');
            return;
          }
          notifyUserAboutUpdate();
        }
      });
      
      // Expose registration for debugging
      window.swRegistration = registration;
      
    } catch (error) {
      console.error('Service Worker registration failed:', error);
    }
  }
}

// Function to notify about updates with auto-refresh countdown
let updateNotified = false;
function notifyUserAboutUpdate() {
  if (updateNotified) return;
  updateNotified = true;

  // If the page is hidden or user is idle, refresh silently
  if (document.visibilityState === 'hidden') {
    console.log('[Update] Page hidden — refreshing silently');
    forceAppRefresh();
    return;
  }

  // Start a 10-second countdown, then auto-refresh
  let countdown = 10;
  const countdownInterval = setInterval(() => {
    countdown--;
    if (countdown <= 0) {
      clearInterval(countdownInterval);
      console.log('[Update] Auto-refresh countdown reached zero');
      forceAppRefresh();
    }
  }, 1000);

  // If user switches away during countdown, refresh immediately
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      clearInterval(countdownInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      console.log('[Update] Page became hidden during countdown — refreshing now');
      forceAppRefresh();
    }
  };
  document.addEventListener('visibilitychange', handleVisibilityChange);

  // Show toast with countdown info
  const showUpdate = () => {
    toast.info('Updating app...', {
      description: 'A new version is available. Refreshing in a few seconds.',
      duration: 12000,
      action: {
        label: 'Update Now',
        onClick: () => {
          clearInterval(countdownInterval);
          document.removeEventListener('visibilitychange', handleVisibilityChange);
          forceAppRefresh();
        }
      }
    });
  };

  if (window.showToast) {
    window.showToast({
      title: 'Updating app...',
      description: 'A new version is available. Refreshing in a few seconds.',
      action: {
        label: 'Update Now',
        onClick: () => {
          clearInterval(countdownInterval);
          document.removeEventListener('visibilitychange', handleVisibilityChange);
          forceAppRefresh();
        }
      }
    });
  } else {
    showUpdate();
  }
}

// Make toast available for service worker updates
window.showToast = ({ title, description, action }) => {
  toast(title, {
    description,
    duration: 12000,
    action: action ? {
      label: action.label,
      onClick: action.onClick
    } : undefined
  });
};

// Manually force refresh for debugging
window.forceAppRefresh = forceAppRefresh;

// Service worker & version check: only on production hosts
if (isPreviewHost()) {
  // Unregister any stale service workers on preview/dev
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then(regs => {
      regs.forEach(r => r.unregister());
      if (regs.length > 0) console.log('[SW] Unregistered stale service workers on preview host');
    });
  }
} else {
  // Production: register SW and start version checks
  registerServiceWorker();
  
  initVersionCheck().catch(error => {
    console.error("Failed to initialize version checking:", error);
  });

  setInterval(() => {
    initVersionCheck().catch(error => {
      console.error("Periodic version check failed:", error);
    });
  }, 5 * 60 * 1000);
}

// Create root and render app
const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found');
}

const root = ReactDOM.createRoot(rootElement);
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

// Add a global type for debugging
declare global {
  interface Window {
    forceAppRefresh: () => void;
    swRegistration?: ServiceWorkerRegistration;
    showToast: (options: {
      title: string;
      description?: string;
      action?: {
        label: string;
        onClick: () => void;
      }
    }) => void;
  }
}
